import {
  getCoverageByValue,
  getCurrencyFormat,
} from '@rabbit/elements/shared-components';
import { DTCaseFlowPrivateProxy, SELF_REGISTERED_VENDABLE } from '@rabbit/data/types';
import { calcClaimCostsTotal, formatWarrantyCostForTable } from './claimCosts';

export function buildFaultIdToTitleMap(faultListData: any): Record<string, string> {
  const map: Record<string, string> = {};
  const list = faultListData ?? [];
  if (Array.isArray(list)) {
    list.forEach((f: any) => {
      if (f?.docid && f?.title) map[String(f.docid)] = String(f.title);
    });
  }
  return map;
}

export function getWarrantyAssessmentLabel(result: DTCaseFlowPrivateProxy): string {
  const {
    goodwill_warranty_final,
    final_assessment,
    goodwill_warranty_initial,
    preliminary_assessment,
  } = result.facts || {};

  const current_assessment =
    goodwill_warranty_final ||
    final_assessment ||
    goodwill_warranty_initial ||
    preliminary_assessment;

  return current_assessment && String(current_assessment).trim() !== ''
    ? getCoverageByValue(String(current_assessment))?.label ?? '-'
    : '-';
}

export function getAssessedFaultText(
  result: DTCaseFlowPrivateProxy,
  faultIdToTitle: Record<string, string>
): string {
  const faults = result?.facts?.holding_faults;
  if (!Array.isArray(faults) || faults.length === 0) return '-';
  const labels = faults
    .map((id: any) => faultIdToTitle[String(id)] ?? String(id))
    .filter(Boolean);
  return labels.length ? labels.join(', ') : '-';
}

export function getProductNameText(result: DTCaseFlowPrivateProxy): string {
  if (result?.facts?.holding_vendable_id === SELF_REGISTERED_VENDABLE) {
    const brand = result?.facts?.srvProductInfo?.brand ?? '';
    const title = result?.facts?.srvProductInfo?.title ?? '';
    const joined = `${brand} ${title}`.trim();
    return joined || '-';
  }
  return result?.facts?.consumer_holding_name ?? '-';
}

export function getDefectivePartText(result: DTCaseFlowPrivateProxy): string {
  const parts = result?.facts?.assessment_parts;
  if (!Array.isArray(parts) || parts.length === 0) return '-';
  const first = parts[0];
  return first?.name ?? first?.id ?? '-';
}

export function getDefectivePartManufacturerText(
  result: DTCaseFlowPrivateProxy
): string {
  const parts = result?.facts?.assessment_parts;
  if (!Array.isArray(parts) || parts.length === 0) return '-';
  const first = parts[0];
  return first?.manufacturer ?? '-';
}

export function getReplacementPartText(result: DTCaseFlowPrivateProxy): string {
  const parts = result?.facts?.parts_used_cost_data;
  if (!Array.isArray(parts) || parts.length === 0) return '-';
  const first = parts[0];
  return first?.name ?? first?.id ?? '-';
}

export function hasAnyDefectivePart(rows: DTCaseFlowPrivateProxy[]): boolean {
  return rows.some((row) => {
    const parts = row?.facts?.assessment_parts;
    if (!Array.isArray(parts) || parts.length === 0) return false;
    const first = parts[0];
    return Boolean(first?.name || first?.id || first?.manufacturer);
  });
}

export function hasAnyReplacementPart(rows: DTCaseFlowPrivateProxy[]): boolean {
  return rows.some((row) => {
    const parts = row?.facts?.parts_used_cost_data;
    if (!Array.isArray(parts) || parts.length === 0) return false;
    const first = parts[0];
    return Boolean(first?.name || first?.id);
  });
}

export function getWarrantyCostText(result: DTCaseFlowPrivateProxy): string {
  const wc = result?.facts?.warranty_cost;
  if (!wc) return '-';
  const parsed = formatWarrantyCostForTable(wc);
  if (!parsed.currency) return parsed.amount ? String(parsed.amount) : '-';
  return getCurrencyFormat(parsed.amount, parsed.currency) ?? '-';
}

export function getClaimCostsTotalText(result: DTCaseFlowPrivateProxy): string {
  const { total, currencies } = calcClaimCostsTotal(result?.facts ?? {});
  if (!total || total <= 0)
    return getCurrencyFormat(0, currencies[0] ?? '') ?? '0';
  if (currencies.length === 1)
    return getCurrencyFormat(total, currencies[0]) ?? String(total);
  return `${total.toFixed(2)} (${currencies.join(',')})`;
}

export function getGoodwillOverrideFlag(
  result: DTCaseFlowPrivateProxy
): 'Yes' | '-' {
  const v =
    result?.facts?.goodwill_override_final ?? result?.facts?.goodwill_override_initial;
  return v === true ? 'Yes' : '-';
}

export function hasVehicleNumber(rows: DTCaseFlowPrivateProxy[]): boolean {
  return (rows || []).some((row) => {
    const value = row?.facts?.vehicle_no ?? row?.facts?.additionalData?.vehicleNo;
    return typeof value === 'string' ? value.trim() !== '' : Boolean(value);
  });
}
